import React from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  LinearProgress,
  CircularProgress,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Campaign,
  Refresh,
  SmartToy,
  Chat,
  Handshake,
  People,
  Share,
  Church,
  Search,
  LiveTv,
  MoreHoriz,
} from "@mui/icons-material";

export interface BreakdownItem {
  source: string;
  label: string;
  count: number;
  percentage: number;
}

export interface RecentFeedback {
  user_id: number;
  username: string;
  referral_source: string;
  referral_label: string;
  referral_detail: string;
  created_at: string | null;
}

export interface AcquisitionStatsData {
  total_surveyed: number;
  total_answered: number;
  total_dismissed: number;
  response_rate: number;
  breakdown: BreakdownItem[];
  recent_feedbacks: RecentFeedback[];
}

const SOURCE_ICONS: Record<string, React.ReactNode> = {
  whatsapp: <Chat sx={{ color: "#25D366", fontSize: 18 }} />,
  funeral_home: <Handshake sx={{ color: "#0d9488", fontSize: 18 }} />,
  friend_family: <People sx={{ color: "#8b5cf6", fontSize: 18 }} />,
  social_media: <Share sx={{ color: "#3b82f6", fontSize: 18 }} />,
  ai_assistant: <SmartToy sx={{ color: "#f59e0b", fontSize: 18 }} />,
  church_community: <Church sx={{ color: "#ec4899", fontSize: 18 }} />,
  google_search: <Search sx={{ color: "#64748b", fontSize: 18 }} />,
  muzukuru_stream: <LiveTv sx={{ color: "#ef4444", fontSize: 18 }} />,
  other: <MoreHoriz sx={{ color: "#94a3b8", fontSize: 18 }} />,
};

interface AcquisitionStatsCardProps {
  data: AcquisitionStatsData | null;
  loading: boolean;
  error: boolean;
  onRefresh: () => void;
}

const AcquisitionStatsCard: React.FC<AcquisitionStatsCardProps> = ({
  data,
  loading,
  error,
  onRefresh,
}) => {

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 3,
        bgcolor: "#ffffff",
        mb: 3,
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              bgcolor: "#f0fdfa",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Campaign sx={{ color: "#0d9488" }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1.1rem", color: "#1e293b" }}>
              How Users Found Chema
            </Typography>
            <Typography variant="body2" sx={{ color: "#64748b", fontSize: "0.85rem" }}>
              How registered Chema organizers and users discovered the platform
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {data && (
            <Chip
              label={`${data.total_answered} Responses (${data.response_rate}% rate)`}
              size="small"
              sx={{ bgcolor: "#f0fdf4", color: "#166534", fontWeight: 600, border: "1px solid #bbf7d0" }}
            />
          )}
          <Tooltip title="Refresh Acquisition Stats">
            <IconButton size="small" onClick={onRefresh} disabled={loading}>
              <Refresh fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={32} sx={{ color: "#0d9488" }} />
        </Box>
      ) : error ? (
        <Typography variant="body2" sx={{ color: "#b91c1c", textAlign: "center", py: 3 }}>
          Could not load onboarding survey responses.
        </Typography>
      ) : !data || data.total_answered === 0 ? (
        <Typography variant="body2" sx={{ color: "#94a3b8", textAlign: "center", py: 3 }}>
          No onboarding survey responses yet. They will appear here as new users answer the prompt.
        </Typography>
      ) : (
        <Grid container spacing={3}>
          {/* Breakdown progress bars */}
          <Grid size={{xs: 12, md: 7}}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {data.breakdown.map((item) => (
                <Box key={item.source} sx={{ width: "100%" }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {SOURCE_ICONS[item.source] || SOURCE_ICONS.other}
                      <Typography sx={{ fontSize: "0.875rem", fontWeight: 600, color: "#334155" }}>
                        {item.label}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, color: "#0f172a" }}>
                      {item.count} <span style={{ color: "#64748b", fontWeight: 400 }}>({item.percentage}%)</span>
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={item.percentage}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      bgcolor: "#f1f5f9",
                      "& .MuiLinearProgress-bar": {
                        bgcolor: "#0d9488",
                        borderRadius: 4,
                      },
                    }}
                  />
                </Box>
              ))}
            </Box>
          </Grid>

          {/* Recent feedback with notes / details */}
          <Grid size={{xs: 12, md: 5}}>
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: "#f8fafc",
                border: "1px solid #edf2f7",
                height: "100%",
                maxHeight: 280,
                overflowY: "auto",
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#475569", mb: 1.5 }}>
                Recent Responses & Notes
              </Typography>
              {data.recent_feedbacks.length === 0 ? (
                <Typography variant="caption" sx={{ color: "#94a3b8" }}>
                  No recent notes.
                </Typography>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.2 }}>
                  {data.recent_feedbacks.map((fb, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        p: 1.2,
                        borderRadius: 1.5,
                        bgcolor: "#ffffff",
                        border: "1px solid #e2e8f0",
                      }}
                    >
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#1e293b" }}>
                          {fb.username}
                        </Typography>
                        <Chip
                          label={fb.referral_label}
                          size="small"
                          sx={{ fontSize: "0.7rem", height: 20, bgcolor: "#f1f5f9", color: "#475569" }}
                        />
                      </Box>
                      {fb.referral_detail && (
                        <Typography sx={{ fontSize: "0.75rem", color: "#64748b", mt: 0.5, fontStyle: "italic" }}>
                          "{fb.referral_detail}"
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
      )}
    </Paper>
  );
};

export default AcquisitionStatsCard;
